/**
 * Dev performance overlay (W3-03; DESIGN.md §10.2 "Measuring": frame breakdown).
 *
 * A small fixed panel (dev builds only, opt-in with `?perf` in the URL or localStorage 'phiedit.dev.perf' = '1')
 * showing, four times a second:
 *   - frames per second and the average / worst time spent in the frame loop's views;
 *   - the time of every registered frame view (transport, preview = engine evaluation + quads + GL submit, each
 *     timeline host, overlays), from the FrameScheduler's profiling;
 *   - extra lines from registered sources (`registerPerfSource`: the preview renderer adds its CPU / GPU time, render
 *     scale and quad count);
 *   - long tasks outside the frame loop (React commits, store work), from a PerformanceObserver where available.
 * It is plain DOM (no React, no app imports) so it measures without disturbing what it measures: it updates on a
 * timer, never schedules frames, and repaints only itself (`contain: strict`).
 */

import type { AppFrameScheduler } from './FrameScheduler';
import catalogue, { m } from './perf.messages';

type Source = () => string | null;

const sources = new Map<string, Source>();
let installed: { readonly el: HTMLElement; readonly dispose: () => void } | null = null;

/** True when the overlay was asked for (`?perf`, or localStorage 'phiedit.dev.perf' = '1'). */
export function perfOverlayRequested(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).has('perf')) return true;
    return window.localStorage.getItem('phiedit.dev.perf') === '1';
  } catch {
    return false;
  }
}

/** True while the overlay is shown (sources may skip their bookkeeping otherwise). */
export function perfOverlayActive(): boolean {
  return installed !== null;
}

/** Adds a line to the overlay (replaces a source of the same name); returns the remover. */
export function registerPerfSource(name: string, fn: Source): () => void {
  sources.set(name, fn);
  return () => {
    if (sources.get(name) === fn) sources.delete(name);
  };
}

/** The English text of a `perf` message (the overlay has no i18n runtime). */
export function perfText(key: string, params: Readonly<Record<string, string | number>> = {}): string {
  const id = key.slice(catalogue.ns.length + 1);
  const raw = catalogue.messages[id] ?? key;
  return raw.replace(/\{(\w+)\}/g, (all, name: string) => {
    const v = params[name];
    return v === undefined ? all : String(v);
  });
}

const fmt = (ms: number): string => (ms >= 10 ? ms.toFixed(0) : ms.toFixed(1));

/** Shows the overlay and turns the scheduler's profiling on (idempotent); returns the remover. */
export function installPerfOverlay(scheduler: AppFrameScheduler): () => void {
  if (installed !== null) return installed.dispose;
  if (typeof document === 'undefined') return () => undefined;
  const el = document.createElement('div');
  el.dataset.perfOverlay = '';
  el.setAttribute('aria-hidden', 'true');
  Object.assign(el.style, {
    position: 'fixed',
    right: '8px',
    bottom: '36px',
    zIndex: '2147483000',
    minWidth: '220px',
    padding: '6px 8px',
    font: '11px/1.35 var(--font-mono, monospace)',
    color: 'var(--color-text, CanvasText)',
    background: 'var(--color-surface-2, Canvas)',
    border: '1px solid var(--color-border, GrayText)',
    borderRadius: '4px',
    opacity: '0.92',
    pointerEvents: 'none',
    whiteSpace: 'pre',
    contain: 'strict',
    height: '0px',
  } satisfies Partial<CSSStyleDeclaration>);
  scheduler.setProfiling(true);
  let longMs = 0;
  let observer: PerformanceObserver | null = null;
  try {
    observer = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) longMs += e.duration;
    });
    observer.observe({ type: 'longtask', buffered: false });
  } catch {
    observer = null;
  }
  let lastAt = performance.now();
  const update = (): void => {
    const p = scheduler.profile();
    const t = performance.now();
    const lines: string[] = [perfText(m.title)];
    if (p.fps <= 0) lines.push(perfText(m.idle));
    else lines.push(perfText(m.fps, { fps: p.fps.toFixed(0) }));
    lines.push(perfText(m.frame, { ms: fmt(p.frameMs), worst: fmt(p.worstMs) }));
    for (const v of p.views) lines.push(perfText(m.view, { name: v.id, ms: fmt(v.ms) }));
    for (const [, fn] of sources) {
      try {
        const line = fn();
        if (line !== null) lines.push(line);
      } catch {
        // a failing source just shows nothing
      }
    }
    if (observer !== null) lines.push(perfText(m.longTasks, { ms: fmt((longMs * 1000) / Math.max(1, t - lastAt)) }));
    longMs = 0;
    lastAt = t;
    el.textContent = lines.join('\n');
    el.style.height = `${String(lines.length * 15 + 12)}px`;
  };
  const timer = setInterval(update, 250);
  const attach = (): void => {
    if (el.parentElement === null) document.body.appendChild(el);
  };
  // document.body is typed non-null; the script may still run before the parser creates it.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attach, { once: true });
  else attach();
  const dispose = (): void => {
    clearInterval(timer);
    observer?.disconnect();
    el.remove();
    scheduler.setProfiling(false);
    installed = null;
  };
  installed = { el, dispose };
  return dispose;
}

export { m as perfMessages };
